import { SessionStatusEnum } from '@/store/engine/openapi/generated';
import { Store } from '@/store/mainStore';
import { LeroyUtils } from '@/store/leroy/LeroyUtils';
import { action, observable } from 'mobx';
import { OrderWithPatient } from '@/store/DoctorStore';
import { Base64 } from 'js-base64';
import UtilsStore from '@/store/utilsStore';
import { SHOE_SIZE_UNIT, SHOE_TYPES, ShoeType } from '@/store/ShoeMeasurements';
import { Order } from '@/store/engine/openapi/generated';
import Prescription from './prescription/prescription-types';
import { Packs } from './ProductStore';
import { Patient } from './engine/openapi/generated/models/Patient';
import PatientStore from './patientStore';

export default class OrderStore {
	store: Store;
	@observable ordering: boolean = false;
	@observable public order?: Order;
	@observable public lastOrder?: Order;
	@observable public quantity: number = 1;

	constructor(store: Store) {
		this.store = store;
	}

	public quantityInc() {
		this.quantity += 1;
	}

	public quantityDec() {
		this.quantity -= 1;
	}

	private convertPackToPacks(pack: 'right' | 'left' | 'both'): Packs {
		switch (pack) {
			case 'right':
				return 'Right Only';
			case 'left':
				return 'Left Only';
			case 'both':
				return 'Pair';
			default:
				// then it's already converted
				return pack;
		}
	}

	private async fillPrescriptionFromOrderData(order: Order) {
		const presc = order.prescriptionObj as Prescription;
		if (!presc) {
			throw new Error('Unable to load prescription object');
		}

		// work around for bugged camelCaseKeys(), it changes neuroma_pad and balance_pad array to object
		await this.store.prescription.getPrescription(presc);
		this.store.product.assignProductPackFromPresc(this.convertPackToPacks(presc.uiContextData.pack));
		this.store._patient.fillUICContextDataFromOrderAttachment(presc.uiContextData);
	}

	public async loadFromOrder(orderID: string) {
		const order = await this.store.engine.order.getOrder(orderID);
		if (!order) {
			throw new Error(`Cannot fetch order of id ${orderID}`);
		}

		this.order = order;
		if (order.sessionID) {
			this.store.biometric.lastValidScanningSessionID = order.sessionID;
		}
		const medicalProfile = this.store._patient.patient?.medicalSolutionData;
		if (medicalProfile) {
			await this.store._patient.fillUIDataWithMedicalProfileForOrder(medicalProfile, order.prescriptionObj?.uiContextData.zebraID);
		}

		const user = this.store._patient.patient?.user;
		if (user) {
			this.store._patient.fillUIDataWithUserProfile(user);
		}

		await this.store._patient.setAssignedDoctor(this.store._patient.patient?.user?.operators?.currentOwner);
		this.store._patient.fillUIDataWithOrderData(order);
		await this.fillPrescriptionFromOrderData(order);
		await this.setupOrderFlags(order);
		await this.setupLeroyMetadata(order);
	}

	private async setupLeroyMetadata(order: Order) {
		this.store.leroy.setMetadataFromBE(order.leroyMeta);
	}

	// this is for keeping compatibility with old state
	get hadOrder() {
		return !!this.order?.orderID;
	}

	async setupOrderFlags(order: Order) {
		if (order?.flags?.expertReview) {
			this.store._patient.patientUIData.expertReview = order.flags.expertReview;
		}

		if (order?.flags?.zebraID) {
			this.store._patient.patientUIData.zebraID = order.flags.zebraID;
		}
	}

	public async reorder(): Promise<Patient> {
		let patient = (await this.store._patient.clonePatient()) ?? {};

		if (this.order?.orderID) {
			this.store._patient.patientUIData.notes += `\n\nOriginal order ID ${this.order.orderID} \n`;
		}

		if (this.store._patient.patientUIData.zebraID) {
			this.store._patient.patientUIData.zebraID = '';
		}

		setTimeout(async () => {
			await this.store._patient.updateMedicalProfile({
				update: true,
				natsData: SessionStatusEnum.SESSION_STATUS_PRESCRIPTION_CREATED
			});
			this.lastOrder = this.order;
			this.order = { orderID: '' };
		}, 1000);

		return Promise.resolve(patient);
	}

	public async createOrder() {
		const shippingAddress = {
			firstName: this.store.doctor.doctorUser?.firstName,
			lastName: this.store.doctor.doctorUser?.lastName,
			...this.store.engine.store.getStore.address
		};

		let items: Array<Record<string, any>> = [];

		{
			const product = {
				customProperties: [],
				sizeSystem: 'us'
			};

			if (!this.store._patient.patientUIData.shoeSizes.differentSizes) {
				let item = {
					product: {
						...product,
						shoeSize: `${this.store._patient.patientUIData.shoeSizes.shoeSize}`,
						sizeGender: `${this.store._patient.patientUIData.shoeUnit.shoeUnit}`
					},
					quantity: this.store.order.quantity,
					shippingAddress
				};
				items.push(item);
			} else {
				for (let type of ['LEFT', 'RIGHT']) {
					let item = {
						product: {
							...product,
							shoeSize: `${this.store._patient.patientUIData.shoeSizes.shoeSizeLR[type === 'LEFT' ? 'L' : 'R']}`,
							leftShoeSize: type === 'LEFT' ? this.store._patient.patientUIData.shoeSizes.shoeSizeLR.L : undefined,
							rightShoeSize: type === 'RIGHT' ? this.store._patient.patientUIData.shoeSizes.shoeSizeLR.R : undefined,
							sizeGender: `${this.store._patient.patientUIData.shoeUnit.shoeUnitLR[type === 'LEFT' ? 'L' : 'R']}`
						},
						quantity: this.store.order.quantity,
						type: type,
						shippingAddress
					};
					items.push(item);
				}
			}
		}

		const orderID = this.store.engine.order.generateOrderID();
		this.store.leroy.leroyManager.metadata = LeroyUtils.injectEmbossedTextAndFilenameToOrder(
			this.store.leroy.leroyManager.metadata,
			orderID,
			this.store._patient.patientUIData.firstName,
			this.store._patient.patientUIData.lastName
		);
		await this.store._patient.updateMedicalProfile({ update: true, natsData: SessionStatusEnum.SESSION_STATUS_PRESCRIPTION_UPDATED });

		await new Promise((r) => setTimeout(r, 2000));

		const order = await this.store.engine.order.order(orderID, items);

		if (!order) {
			this.store.utils.pushMessage('Something went wrong while creating order');
			throw new Error('Something went wrong while creating order');
		}

		try {
			await this.store.engine.order.flagsOrder(order.orderID, this.store._patient.patientUIData.expertReview);
		} catch (e) {
			console.error(e);
			this.store.utils.pushMessage('Something went wrong while setting order flags');
		}

		return order as Order;
	}
}
